import axios from "axios";
import {

    cardDeleteFail,
    cardDeleteRequest,
    cardDeleteSuccess,

    cardListFail,
    cardListRequest,
    cardListSuccess,

    cardUpdateFail,
    cardUpdateRequest,
    cardUpdateSuccess,

    cardCreateRequest,
    cardCreateSuccess,
    cardCreateFail,

} from "../reducers/cardReducers";

// Function to get CSRF token from cookie
const getCsrfToken = () => {
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

// Debug function to check all cookies
const getAllCookies = () => {
    const cookies = {};
    if (document.cookie && document.cookie !== '') {
        document.cookie.split(';').forEach(cookie => {
            const parts = cookie.trim().split('=');
            if (parts.length === 2) {
                cookies[parts[0]] = decodeURIComponent(parts[1]);
            }
        });
    }
    return cookies;
}

const createAPIinstance = () => {
    const csrfToken = getCsrfToken();
    getAllCookies(); // Log all cookies for debugging
    
    // Define the URL for card-related API requests
    const baseURL = `${process.env.REACT_APP_BASE_API_URL}/api/users/cards`;
    
    // Include CSRF token in headers
    const headers = {
        'Content-Type': 'application/json',
    };
    
    if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
    }
    
    return axios.create({
        baseURL: baseURL,
        headers: headers,
        withCredentials: true // This ensures cookies are sent with the request
    });
}

// First fetch CSRF token before any card operations
export const fetchCsrfToken = () => async () => {
    try {
        // Make a request to a CSRF endpoint to get a fresh token
        await axios.get(`${process.env.REACT_APP_BASE_API_URL}/api/users/csrf/`, {
            withCredentials: true
        });
        return true;
    } catch (error) {
        return false;
    }
};

export const listCards = () => async (dispatch) => {
    try {
        // First fetch a fresh CSRF token
        await dispatch(fetchCsrfToken());
        
        dispatch(cardListRequest());

        const api = createAPIinstance();
        
        const { data } = await api.get('/');
        
        dispatch(cardListSuccess(data))
    } catch(error) {
        dispatch(cardListFail(
            error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message
        ))
    }
};

export const createCard = (card) => async (dispatch) => {
    try {
        // First fetch a fresh CSRF token
        await dispatch(fetchCsrfToken());
        
        dispatch(cardCreateRequest());

        const api = createAPIinstance();
        
        // Format and validate the card data
        const formattedCard = {
            cardOwner: card.cardOwner.trim(),
            cardNumber: card.cardNumber.replace(/\s/g, ''),
            expDate: card.expDate,
            ccv: card.ccv.trim(),
            cardType: card.cardType,
            cardBrand: card.cardBrand
        };
        
        // Log the request data for debugging
        console.log('Creating card with data:', formattedCard);
        
        const { data } = await api.post('/', formattedCard);
        
        // Refresh the card list after successful creation
        await dispatch(listCards());
        dispatch(cardCreateSuccess(data));
        
        return data;
    } catch(error) {
        console.error('Card creation error:', error.response?.data || error.message);
        
        // Handle different types of errors
        let errorMessage = 'Failed to create card';
        if (error.response?.data) {
            if (typeof error.response.data === 'string') {
                errorMessage = error.response.data;
            } else if (error.response.data.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.response.data.cardNumber?.detail) {
                errorMessage = error.response.data.cardNumber.detail;
            } else {
                // Get the first error message from the response
                const firstError = Object.values(error.response.data)[0];
                errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
            }
        }
        
        dispatch(cardCreateFail(errorMessage));
        throw new Error(errorMessage);
    }
};

export const updateCard = (card) => async (dispatch) => {
    try {
        // First fetch a fresh CSRF token
        await dispatch(fetchCsrfToken());
        
        dispatch(cardUpdateRequest());

        const api = createAPIinstance();
        const { data } = await api.put(
            `/${card._id}/`,
            card
        );

        await dispatch(cardUpdateSuccess(data))
        dispatch(listCards())
    } catch(error) {
        dispatch(cardUpdateFail(
            error.response && error.response.data.cardNumber && error.response.data.cardNumber.detail
            ? error.response.data.cardNumber.detail
            : error.message
        ))
    }
};

export const deleteCard = (id) => async (dispatch) => {
    try {
        // First fetch a fresh CSRF token
        await dispatch(fetchCsrfToken());
        
        dispatch(cardDeleteRequest());

        const api = createAPIinstance();
        const { data } = await api.delete(`/${id}/`);

        await dispatch(cardDeleteSuccess(data))
        dispatch(listCards())
    } catch(error) {
        dispatch(cardDeleteFail(
            error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message
        ))
    }
};
